/**
 * Serviço WebSocket para Atualizações de Status de Upload em Tempo Real
 *
 * CONTEXTO DE NEGÓCIO:
 * Este serviço gerencia a conexão WebSocket com o backend para receber
 * atualizações de status de uploads de documentos em tempo real. Substitui
 * o mecanismo de polling, oferecendo notificações instantâneas quando o
 * status de um upload muda no backend.
 *
 * OBJETIVO:
 * - Estabelecer conexão WebSocket com o endpoint `/ws/documentos/status`
 * - Gerenciar o ciclo de vida da conexão (abrir, fechar, reconectar)
 * - Fornecer interface de subscrição para componentes receberem atualizações
 * - Tratar erros e desconexões de forma robusta
 *
 * DECISÕES DE ARQUITETURA:
 * - Singleton: apenas uma instância do serviço para toda a aplicação
 * - Reconexão automática em caso de falha
 * - Sistema de callbacks para notificar componentes inscritos
 * - Tipagem forte para garantir consistência das mensagens
 *
 * INTEGRAÇÃO COM BACKEND:
 * - Conecta ao endpoint: ws://<backend>/ws/documentos/status
 * - Recebe mensagens no formato RespostaStatusUpload (compatível com API de polling)
 *
 * USO:
 * ```typescript
 * const servico = obterWebSocketUploadService();
 *
 * // Inscrever para receber atualizações
 * const unsubscribe = servico.subscribe((atualizacao) => {
 *   console.log('Atualização recebida:', atualizacao);
 * });
 *
 * // Conectar ao WebSocket
 * servico.connect();
 *
 * // Ao desmontar componente, desinscrever e desconectar
 * unsubscribe();
 * servico.disconnect();
 * ```
 */

/**
 * Tipo que representa uma atualização de status de upload recebida via WebSocket
 *
 * ESTRUTURA:
 * - Compatível com o formato retornado pela API de polling (RespostaStatusUpload)
 * - Permite que componentes usem a mesma lógica para polling e WebSocket
 */
export interface AtualizacaoStatusUpload {
  /** ID único do upload */
  upload_id: string;

  /** Status atual do upload */
  status: 'INICIADO' | 'SALVANDO' | 'PROCESSANDO' | 'CONCLUIDO' | 'ERRO';

  /** Descrição da etapa atual do processamento */
  etapa_atual: string;

  /** Progresso do upload (0-100) */
  progresso_percentual: number;

  /** Timestamp da última atualização (formato ISO 8601) */
  timestamp_atualizacao: string;

  /** Mensagem de erro (se status === 'ERRO') */
  mensagem_erro?: string | null;

  /** Lista de arquivos extraídos (para uploads ZIP) */
  arquivos_extraidos?: string[] | null;

  /** Lista de arquivos ignorados por tipo não suportado (para uploads ZIP) */
  arquivos_ignorados?: Array<{
    nome_arquivo: string;
    extensao: string;
    motivo: string;
  }> | null;

  /** Progresso por arquivo (para uploads ZIP) */
  progresso_por_arquivo?: Record<string, { progresso: number; etapa: string }> | null;
}

/**
 * Tipo de função callback para receber atualizações
 */
export type CallbackAtualizacaoUpload = (atualizacao: AtualizacaoStatusUpload) => void;

/**
 * Estados possíveis da conexão WebSocket
 */
type EstadoConexao = 'desconectado' | 'conectando' | 'conectado' | 'erro';

/**
 * Classe que gerencia a conexão WebSocket e notificações para uploads
 */
class WebSocketUploadService {
  /** Instância do WebSocket nativo */
  private websocket: WebSocket | null = null;

  /** Estado atual da conexão */
  private estadoConexao: EstadoConexao = 'desconectado';

  /** Lista de callbacks inscritos para receber atualizações */
  private callbacks: Set<CallbackAtualizacaoUpload> = new Set();

  /** URL do endpoint WebSocket no backend */
  private urlWebSocket: string;

  /** Token JWT para autenticação */
  private tokenJwt: string | null = null;

  /** Upload ID para o WebSocket */
  private uploadId: string | null = null;

  /** Timer para reconexão automática em caso de falha */
  private timerReconexao: ReturnType<typeof setTimeout> | null = null;

  /** Intervalo de reconexão em milissegundos (5 segundos) */
  private readonly INTERVALO_RECONEXAO_MS = 5000;

  /** Número máximo de tentativas de reconexão automática */
  private readonly MAX_TENTATIVAS_RECONEXAO = 10;

  /** Contador de tentativas de reconexão */
  private tentativasReconexao: number = 0;

  /**
   * Contador de referências: quantos componentes estão usando o serviço
   * CONTEXTO: Como este é um singleton, múltiplos componentes podem usá-lo.
   * Só devemos desconectar quando TODOS os componentes tiverem sido desmontados.
   */
  private contadorReferencias: number = 0;

  /** Flag para prevenir múltiplas tentativas de conexão simultâneas */
  private tentandoConectar: boolean = false;

  constructor(tokenJwt?: string, uploadId?: string) {
    this.tokenJwt = tokenJwt || null;
    this.uploadId = uploadId || null;

    // Determinar URL do WebSocket baseado no ambiente
    // CONTEXTO: Em desenvolvimento, backend roda em localhost:8000
    //           Em produção, usar o mesmo host do frontend
    const protocolo = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = import.meta.env.VITE_API_URL
      ? import.meta.env.VITE_API_URL.replace(/^https?:\/\//, '').replace(/\/$/, '')
      : window.location.host;

    // Construir URL base: ws://localhost:8000/ws/documentos/status/{upload_id}
    let urlBase = `${protocolo}//${host}/ws/documentos/status`;
    if (this.uploadId) {
      urlBase += `/${this.uploadId}`;
    }

    // Adicionar token JWT como parâmetro de query se disponível
    if (this.tokenJwt) {
      const urlComToken = new URL(urlBase);
      urlComToken.searchParams.set('token', this.tokenJwt);
      urlBase = urlComToken.toString();
    }

    this.urlWebSocket = urlBase;

    console.log('[WebSocketUploadService] Inicializado com URL:', this.urlWebSocket);
  }

  /**
   * Obtém o token JWT atual usado pelo serviço
   *
   * @returns Token JWT ou null se não definido
   */
  public getTokenJwt(): string | null {
    return this.tokenJwt;
  }

  /**
   * Verifica se o token JWT fornecido é diferente do atual
   *
   * @param tokenJwt - Token JWT a comparar
   * @returns true se os tokens são diferentes
   */
  public tokenJwtDiferente(tokenJwt?: string): boolean {
    return this.tokenJwt !== (tokenJwt || null);
  }

  /**
   * Verifica se o uploadId fornecido é diferente do atual
   *
   * @param uploadId - Upload ID a comparar
   * @returns true se os uploadIds são diferentes
   */
  public uploadIdDiferente(uploadId?: string): boolean {
    return this.uploadId !== (uploadId || null);
  }

  /**
   * Inscreve um callback para receber atualizações de status de upload
   *
   * @param callback - Função a ser chamada quando uma atualização for recebida
   * @returns Função para cancelar a inscrição
   *
   * EXEMPLO:
   * ```typescript
   * const unsubscribe = servico.subscribe((update) => {
   *   console.log('Nova atualização:', update);
   * });
   *
   * // Mais tarde, para cancelar:
   * unsubscribe();
   * ```
   */
  subscribe(callback: CallbackAtualizacaoUpload): () => void {
    this.callbacks.add(callback);
    console.log('[WebSocketUploadService] Callback inscrito. Total de callbacks:', this.callbacks.size);

    // Retorna função para cancelar inscrição
    return () => {
      this.callbacks.delete(callback);
      console.log('[WebSocketUploadService] Callback removido. Total de callbacks:', this.callbacks.size);
    };
  }

  /**
   * Estabelece conexão com o WebSocket do backend para uploads
   *
   * COMPORTAMENTO:
   * - Se já estiver conectado, apenas incrementa o contador de referências
   * - Se estiver tentando conectar, aguarda a conclusão
   * - Caso contrário, inicia nova conexão
   *
   * THREAD-SAFETY:
   * Método é thread-safe através do uso de flags e verificações de estado.
   * Múltiplas chamadas simultâneas são tratadas adequadamente.
   */
  connect(): void {
    // Importar telemetria (lazy para evitar dependência circular)
    import('./servicoTelemetria').then(({ telemetria }) => {
      telemetria.registrarEvento('WEBSOCKET_CONNECT_TENTATIVA', 'DOCUMENT_UPLOAD', {
        upload_id: this.uploadId,
        estado_atual: this.estadoConexao,
        tentativas_reconexao: this.tentativasReconexao
      }, 'WebSocketUploadService');
    });

    // Se já conectado, apenas incrementar referência
    if (this.estadoConexao === 'conectado') {
      this.contadorReferencias++;
      console.log(`[WebSocketUploadService] Já conectado. Referências: ${this.contadorReferencias}`);
      return;
    }

    // Se já tentando conectar, aguardar
    if (this.tentandoConectar) {
      console.log('[WebSocketUploadService] Já tentando conectar, aguardando...');
      return;
    }

    // Marcar que estamos tentando conectar
    this.tentandoConectar = true;
    this.contadorReferencias++;

    try {
      console.log('[WebSocketUploadService] Iniciando conexão...');
      this.estadoConexao = 'conectando';

      // Criar nova instância do WebSocket
      this.websocket = new WebSocket(this.urlWebSocket);

      // Configurar event handlers
      this.websocket.onopen = this._onOpen.bind(this);
      this.websocket.onmessage = this._onMessage.bind(this);
      this.websocket.onclose = this._onClose.bind(this);
      this.websocket.onerror = this._onError.bind(this);

    } catch (erro) {
      console.error('[WebSocketUploadService] Erro ao criar WebSocket:', erro);
      this.estadoConexao = 'erro';
      this.tentandoConectar = false;
      this._agendarReconexao();
    }
  }

  /**
   * Desconecta do WebSocket
   *
   * COMPORTAMENTO:
   * - Decrementa contador de referências
   * - Só desconecta fisicamente quando contador chegar a zero
   * - Cancela timers de reconexão se existirem
   *
   * THREAD-SAFETY:
   * Método é thread-safe através do uso de contador de referências.
   */
  disconnect(): void {
    this.contadorReferencias = Math.max(0, this.contadorReferencias - 1);

    console.log(`[WebSocketUploadService] Disconnect chamado. Referências restantes: ${this.contadorReferencias}`);

    // Só desconectar fisicamente se não há mais referências
    if (this.contadorReferencias === 0) {
      this._desconectarFisicamente();
    }
  }

  /**
   * Força desconexão física independente do contador de referências
   *
   * USO INTERNO:
   * Chamado automaticamente quando ocorrem erros críticos ou quando
   * o contador de referências chega a zero.
   */
  private _desconectarFisicamente(): void {
    console.log('[WebSocketUploadService] Desconectando fisicamente...');

    // Cancelar timer de reconexão se existir
    if (this.timerReconexao) {
      clearTimeout(this.timerReconexao);
      this.timerReconexao = null;
    }

    // Fechar WebSocket se existir
    if (this.websocket) {
      try {
        this.websocket.close(1000, 'Cliente desconectando');
      } catch (erro) {
        console.warn('[WebSocketUploadService] Erro ao fechar WebSocket:', erro);
      }
      this.websocket = null;
    }

    this.estadoConexao = 'desconectado';
    this.tentativasReconexao = 0;
    this.tentandoConectar = false;
  }

  /**
   * Força desconexão completa (método público para uso externo)
   */
  public forcarDesconexao(): void {
    this.contadorReferencias = 0;
    this._desconectarFisicamente();
  }

  /**
   * Handler para evento de abertura da conexão WebSocket
   */
  private _onOpen(): void {
    console.log('[WebSocketUploadService] ✅ WebSocket conectado');
    this.estadoConexao = 'conectado';
    this.tentativasReconexao = 0;
    this.tentandoConectar = false;
  }

  /**
   * Handler para mensagens recebidas via WebSocket
   *
   * FORMATO ESPERADO:
   * ```json
   * {
   *   "upload_id": "uuid-123",
   *   "status": "PROCESSANDO",
   *   "etapa_atual": "Extraindo texto do PDF",
   *   "progresso_percentual": 25,
   *   "timestamp_atualizacao": "2024-01-01T12:00:00Z",
   *   "mensagem_erro": null,
   *   "arquivos_extraidos": ["doc1.pdf", "doc2.pdf"]
   * }
   * ```
   */
  private _onMessage(event: MessageEvent): void {
    try {
      const rawData = event.data as string;
      
      // CORREÇÃO: O backend pode enviar múltiplas mensagens JSON concatenadas
      // quando broadcasts acontecem muito rapidamente.
      // Ex: {"upload_id":"..."}{"upload_id":"..."} (dois JSONs grudados)
      // Solução: Dividir por "}{" e processar cada JSON separadamente
      const jsonMessages = this._splitConcatenatedJson(rawData);
      
      for (const jsonStr of jsonMessages) {
        try {
          const data: AtualizacaoStatusUpload = JSON.parse(jsonStr);

          // Validar estrutura básica da mensagem
          if (!data.upload_id || !data.status) {
            console.warn('[WebSocketUploadService] Mensagem inválida recebida:', data);
            continue;
          }

          console.log('[WebSocketUploadService] 📨 Atualização recebida:', data);

          // Notificar todos os callbacks inscritos
          this.callbacks.forEach(callback => {
            try {
              callback(data);
            } catch (erro) {
              console.error('[WebSocketUploadService] Erro em callback:', erro);
            }
          });
        } catch (parseError) {
          console.warn('[WebSocketUploadService] Falha ao parsear JSON individual:', jsonStr, parseError);
        }
      }

    } catch (erro) {
      console.error('[WebSocketUploadService] Erro ao processar mensagem:', erro, 'Dados recebidos:', event.data);
    }
  }

  /**
   * Divide uma string com múltiplos JSONs concatenados em um array de JSONs individuais.
   * 
   * Exemplo de entrada: '{"a":1}{"b":2}{"c":3}'
   * Saída: ['{"a":1}', '{"b":2}', '{"c":3}']
   */
  private _splitConcatenatedJson(rawData: string): string[] {
    if (!rawData || rawData.trim() === '') {
      return [];
    }
    
    // Estratégia: dividir por "}{" que indica fronteira entre JSONs
    // Depois adicionar de volta as chaves removidas pela divisão
    const parts = rawData.split(/\}\s*\{/);
    
    if (parts.length === 1) {
      // Apenas um JSON, retornar como está
      return [rawData.trim()];
    }
    
    // Múltiplos JSONs: reconstruir adicionando chaves de volta
    return parts.map((part, index) => {
      let json = part.trim();
      
      // Primeiro elemento: pode estar faltando "}" no final
      if (index === 0) {
        if (!json.endsWith('}')) {
          json += '}';
        }
      }
      // Último elemento: pode estar faltando "{" no início
      else if (index === parts.length - 1) {
        if (!json.startsWith('{')) {
          json = '{' + json;
        }
      }
      // Elementos do meio: faltam ambos "{" e "}"
      else {
        if (!json.startsWith('{')) {
          json = '{' + json;
        }
        if (!json.endsWith('}')) {
          json += '}';
        }
      }
      
      return json;
    });
  }

  /**
   * Handler para fechamento da conexão WebSocket
   */
  private _onClose(event: CloseEvent): void {
    console.log(`[WebSocketUploadService] 🔌 WebSocket fechado. Código: ${event.code}, Razão: ${event.reason}`);

    this.estadoConexao = 'desconectado';
    this.websocket = null;
    this.tentandoConectar = false;

    // Tentar reconectar se não foi fechamento intencional
    if (event.code !== 1000) {
      this._agendarReconexao();
    }
  }

  /**
   * Handler para erros na conexão WebSocket
   */
  private _onError(event: Event): void {
    console.error('[WebSocketUploadService] ❌ Erro no WebSocket:', event);
    this.estadoConexao = 'erro';
    this.tentandoConectar = false;
    this._agendarReconexao();
  }

  /**
   * Agenda tentativa de reconexão automática
   *
   * LÓGICA:
   * - Espera INTERVALO_RECONEXAO_MS (5s) antes de tentar
   * - Limita a MAX_TENTATIVAS_RECONEXAO (10) tentativas
   * - Usa backoff exponencial (não implementado ainda)
   */
  private _agendarReconexao(): void {
    // Cancelar reconexão anterior se existir
    if (this.timerReconexao) {
      clearTimeout(this.timerReconexao);
    }

    // Verificar limite de tentativas
    if (this.tentativasReconexao >= this.MAX_TENTATIVAS_RECONEXAO) {
      console.error(`[WebSocketUploadService] Máximo de tentativas de reconexão atingido (${this.MAX_TENTATIVAS_RECONEXAO}). Abortando.`);
      return;
    }

    this.tentativasReconexao++;

    console.log(`[WebSocketUploadService] Agendando reconexão em ${this.INTERVALO_RECONEXAO_MS}ms (tentativa ${this.tentativasReconexao}/${this.MAX_TENTATIVAS_RECONEXAO})`);

    this.timerReconexao = setTimeout(() => {
      console.log('[WebSocketUploadService] Tentando reconectar...');
      this.connect();
    }, this.INTERVALO_RECONEXAO_MS);
  }

  /**
   * Obtém o estado atual da conexão
   *
   * @returns Estado atual da conexão
   */
  public getEstadoConexao(): EstadoConexao {
    return this.estadoConexao;
  }

  /**
   * Verifica se o WebSocket está conectado
   *
   * @returns true se conectado, false caso contrário
   */
  public isConectado(): boolean {
    return this.estadoConexao === 'conectado' && this.websocket?.readyState === WebSocket.OPEN;
  }
}

/**
 * Instância singleton do serviço WebSocket para uploads
 */
let instanciaWebSocketUpload: WebSocketUploadService | null = null;

/**
 * Obtém a instância singleton do serviço WebSocket para uploads
 *
 * @param tokenJwt - Token JWT opcional para autenticação
 * @param uploadId - Upload ID para o WebSocket
 * @returns Instância do serviço
 */
export function obterWebSocketUploadService(tokenJwt?: string, uploadId?: string): WebSocketUploadService {
  // Se não existe instância ou token/uploadId mudou, criar nova
  if (!instanciaWebSocketUpload || instanciaWebSocketUpload.tokenJwtDiferente(tokenJwt) || instanciaWebSocketUpload.uploadIdDiferente(uploadId)) {
    if (instanciaWebSocketUpload) {
      console.log('[WebSocketUploadService] Token JWT ou Upload ID mudou, recriando instância...');
      instanciaWebSocketUpload.forcarDesconexao();
    }

    instanciaWebSocketUpload = new WebSocketUploadService(tokenJwt, uploadId);
  }

  return instanciaWebSocketUpload;
}